import type { ResearchStatus } from '../../hooks/queries/use-research';

/**
 * FRONT-1601 — pure decision logic for the deep-research panel's "finished"
 * state (same reasoning as `research-panel-open.ts` and `finish-now-gate.ts`:
 * unit-testable without React, one definition the panel and its test both
 * consult so the two can't drift).
 *
 * ## The problem this exists to fix
 *
 * The expanded detail panel is `absolute inset-0` over the conversation
 * (`chat-research-banner.tsx`). The instant a run stops being `running`,
 * `isResearchBannerVisible` (`research-banner-logic.ts`) goes false and the
 * whole panel unmounts — a user WATCHING the run sees it vanish with no
 * completion signal. The briefing has in fact landed in the chat behind it,
 * but nothing on screen says so. This function decides when to keep the
 * panel up in a FINISHED state instead of letting it disappear, so the
 * caller can show a "Research finished — here's where to look" message
 * until the user is done with it.
 *
 * ## The four rules
 *
 * 1. **Only `completed`.** A `cancelled` run (Stop, or a Finish-now that
 *    found nothing usable — `deep.ts`'s `usable.length === 0 &&
 *    finishRequested` branch) or a `failed` run keeps today's behaviour and
 *    vanishes outright — pointing someone at a briefing that does not exist
 *    is worse than silence, and a toast already covers the empty-finish
 *    case (see `inbox-chat-modal.tsx`'s finish-cancelled-toast effect).
 * 2. **Only when the panel was EXPANDED at the moment the run finished.**
 *    `panelWasOpenWhenFinished` is the caller's own `researchDetailsOpen`
 *    state, read live rather than snapshotted at the transition: nothing
 *    else is allowed to flip that state back to `true` for an already-
 *    settled run (the auto-open effect in `research-panel-open.ts` bails
 *    the instant `runningResearchId` is `null`, which it always is once a
 *    run settles), so "is it open right now" and "was it open when this run
 *    finished" agree for as long as this predicate keeps returning `true` —
 *    the moment the caller closes the panel, both this rule AND rule 3 stop
 *    it coming back. If the user had already collapsed it, the bar
 *    vanishing on its own is correct: a lingering notice would nag, and the
 *    briefing arrives in the chat by itself regardless.
 * 3. **It must not re-appear once dismissed.** `dismissedRunId` is the
 *    caller's memory of the last run id whose notice was explicitly closed
 *    (button, collapsing the panel, or sending a message — every one of
 *    those paths ends in the SAME `researchDetailsOpen(false)`, so the
 *    caller only needs one place to mark it). Keyed on the run's own id,
 *    not a plain boolean — mirroring `autoOpenedResearchRunIdRef` in
 *    `research-panel-open.ts` — so a SECOND research run in the same chat
 *    gets its own notice: a fresh id can never equal a stale
 *    `dismissedRunId` left over from the run before it.
 * 4. **No run id, no notice.** `runId == null` covers both "no run at all"
 *    and the brief optimistic window before a real run row exists, neither
 *    of which can ever be `completed`.
 *
 * `dismissedRunId` is deliberately typed as `number | null` (the run row's
 * OWN id type — see `ResearchRun.id` in `use-research.ts`) rather than the
 * stringified `ResearchRunId` `research-panel-open.ts` uses for
 * `runningResearchId`: that string form exists there only because a
 * completed run's id is never available as `runningResearchId` (it goes
 * `null` the instant a run stops running), so there is nothing to gain from
 * round-tripping this run's real numeric id through a string.
 */
export function researchFinishedNotice(input: {
  /** The research run's current status, or `undefined` before the query has
   *  resolved (or when there has never been a run). */
  status: ResearchStatus | undefined;
  /** The run's own numeric id, or `null` when there is no run (or none
   *  confirmed yet — the optimistic window has no row to be `completed`). */
  runId: number | null;
  /** The caller's live `researchDetailsOpen` state — see rule 2 above for
   *  why "live" and "at the moment it finished" are the same question here. */
  panelWasOpenWhenFinished: boolean;
  /** The run id this notice has already been dismissed for, or `null` if
   *  nothing has been dismissed yet. */
  dismissedRunId: number | null;
}): boolean {
  const { status, runId, panelWasOpenWhenFinished, dismissedRunId } = input;
  if (status !== 'completed') return false;
  if (runId == null) return false;
  if (!panelWasOpenWhenFinished) return false;
  if (dismissedRunId === runId) return false;
  return true;
}
